import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../lib/auth";
import { ArrowRight, Loader2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_91fc1071-a755-4ee2-8eeb-0c8b2e7c23e2/artifacts/frr6xg90_Product%20Icon%401x.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(formatApiErrorDetail(detail));
      } else if (err.message) {
        setError(`Network error: ${err.message}. Please check your connection and try again.`);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-900 text-white grain flex">
      <div className="hidden lg:flex flex-col justify-between flex-1 p-12 bg-navy-800 border-r border-white/5">
        <Link to="/" className="flex items-center gap-3" data-testid="login-logo">
          <img src={LOGO_URL} alt="Avoir la pêche" className="h-12 w-12 rounded-2xl border border-white/10" />
          <div className="font-heading font-bold text-lg">Avoir la pêche</div>
        </Link>
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-peach-500 font-bold mb-4">Welcome back</div>
          <h2 className="font-heading font-bold text-4xl leading-tight tracking-tight max-w-md">
            The conversation continues,<br />and so does your French.
          </h2>
          <p className="mt-6 text-[#A89BC9] max-w-md leading-relaxed">
            Sign in to pick up your lessons, your flashcards, and your daily streak right where you left off.
          </p>
        </div>
        <div className="text-xs text-[#A89BC9]">© {new Date().getFullYear()} Avoir la pêche</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="Avoir la pêche" className="h-10 w-10 rounded-xl" />
            <div className="font-heading font-bold">Avoir la pêche</div>
          </Link>
          <h1 className="font-heading font-bold text-3xl mb-2">Sign in</h1>
          <p className="text-[#A89BC9] text-sm mb-8">Enter your credentials to continue.</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A89BC9] font-semibold">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-2 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder:text-[#A89BC9]/50 focus:border-peach-500 focus:ring-1 focus:ring-peach-500 outline-none transition"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A89BC9] font-semibold">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-2 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder:text-[#A89BC9]/50 focus:border-peach-500 focus:ring-1 focus:ring-peach-500 outline-none transition"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-2xl px-4 py-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-peach-500 text-navy-900 font-semibold py-3.5 rounded-full hover:bg-peach-400 transition peach-glow flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <div className="mt-8 text-sm text-[#A89BC9] text-center">
            No account yet?{" "}
            <Link to="/register" data-testid="login-to-register-link" className="text-peach-300 hover:text-peach-400 font-semibold">
              Create one
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
